import * as THREE from 'three';
import { chamferBox, plainBox, tubeZ, rodZ, pipeZ, ringZ, mergeGeom, place, translate, rotate } from './primitives.js';
import { vignetteTexture } from './materials.js';
import { discZ } from './parts.js';

/**
 * Sights.
 *
 * Every optic in the game is built here, by one function per family, because the two
 * most expensive bugs this project has shipped both lived in optic geometry and both
 * were *copies*:
 *
 *  1. **Solid geometry through the bore.** The marksman rifle's turret saddle was a
 *     34 x 34 x 16 mm box centred on the optical axis inside a 19.8 mm tube — a
 *     literal plug — and its windage turret was built inside-out, because
 *     `rotateY(+PI/2)` maps the lathe's +Z onto +X and the turret therefore grew
 *     *through* the tube rather than out of it. The scope rendered pure black.
 *  2. **Lit interiors.** The shared `gunmetal` carries bright bare-steel edge wear, so
 *     the moment the glass became see-through the inside of every tube read as a lit
 *     tunnel.
 *
 * The invariants that follow from those, enforced in one place below:
 *
 *  - Nothing whose radius is smaller than the tube's inner wall may exist inside the
 *    tube. Turrets start *outside* the wall; saddles are collars *around* it, built
 *    from an outer wall plus two end faces rather than a `pipeZ`, because a pipe
 *    carries an inward-facing inner wall and anything inward-facing inside a tube is
 *    drawn straight into the sight picture.
 *  - `rotateY(-PI/2)` for a lateral turret, never `+`. The sign is checked by
 *    `_lathedKnob` so no caller can get it wrong again.
 *  - Every tube gets a matte-black `bore` liner, and the liner's front rim sits at the
 *    aperture that was *already* the tightest, 0.6–0.8 mm inside it. Putting it any
 *    further forward at a smaller radius is what turns a scope into a drinking straw
 *    (measured: a 15.6 mm sleeve ending 34 cm from the eye clips the picture to 2.3
 *    degrees). Putting it nowhere is what makes a scope aimed at the sky into a ring
 *    of sunlit machined parts with a disc of world in the middle.
 *  - The objective shoulder — the one inward-facing cone the eye can see all the way
 *    down the bore — is shadowed by a `bore`-material cone 0.3 mm inside it.
 *
 * Every builder returns an `apertures` list: the [radius, z] of each thing that can
 * clip the sight picture. That makes the clear half-cone computable in closed form
 * (`clearCone` below) instead of only discoverable by raycasting after the fact, so a
 * blocked sight can be caught while the numbers are being authored rather than in a
 * screenshot three hours later.
 */

/** Viewmodel scale. Model metres are multiplied by this before they reach the eye. */
export const VIEW_SCALE = 0.86;

/**
 * The clear half-cone, in degrees, that a set of apertures allows.
 *
 * View-space distance from the eye to a model point at `z` is
 * `adsDistance + scale * (aimZ - z)`, because the ADS pose is solved so the aim point
 * lands exactly `adsDistance` in front of the eye on the camera axis.
 */
export function clearCone(apertures, aimZ, adsDistance, scale = VIEW_SCALE) {
  // An open sight forms no tunnel at all. 12 degrees is the practical ceiling: past
  // that the viewmodel's own housing leaves the frame and the number stops meaning
  // anything, so reporting it as unbounded would be worse than reporting it as wide.
  if (!apertures || !apertures.length) return { deg: 12, limiter: { what: 'open sight (no rim)' } };
  let best = Infinity;
  let which = null;
  for (const a of apertures) {
    const d = adsDistance + scale * (aimZ - a.z);
    if (d <= 1e-4) continue;
    const deg = (Math.atan((scale * a.r) / d) * 180) / Math.PI;
    if (deg < best) {
      best = deg;
      which = a;
    }
  }
  return { deg: best, limiter: which };
}

/** Angular diameter of the rear glass, in degrees — the "how big is the sight" number. */
export function glassSubtense(ocularR, ocularZ, aimZ, adsDistance, scale = VIEW_SCALE) {
  const d = adsDistance + scale * (aimZ - ocularZ);
  return (2 * Math.atan((scale * ocularR) / d) * 180) / Math.PI;
}

/* -------------------------------------------------------------------------- */
/* shared sub-assemblies                                                       */
/* -------------------------------------------------------------------------- */

/**
 * A lathed knob (turret, parallax dial, battery cap) growing along a body axis.
 *
 * `axis` is 'up' or 'left'. There is deliberately no way to ask for a knob that grows
 * inward: this function owns the rotation signs precisely so the windage-turret bug
 * cannot be written a second time. `base` must be at least the tube's outer radius,
 * and is asserted below.
 */
function _lathedKnob(base, height, radius, z, axis, tubeR) {
  if (base < tubeR - 1e-6) {
    // Not a throw — a broken turret should not take the weapon layer down — but this
    // is the exact geometry that made the marksman rifle's scope opaque.
    console.warn(`[optics] knob base ${base} is inside tube radius ${tubeR}; clamping`);
    base = tubeR;
  }
  const g = tubeZ(
    [
      [0.0, 0.0],
      [radius, 0.0],
      [radius, height - 0.0016],
      [radius - 0.0016, height],
      [0.0, height],
    ],
    12
  );
  if (axis === 'up') {
    g.rotateX(-Math.PI / 2); // +Z -> +Y
    g.translate(0, base, z);
  } else {
    g.rotateY(-Math.PI / 2); // +Z -> -X. The other sign grows through the bore.
    g.translate(-base, 0, z);
  }
  return g;
}

/**
 * Turret saddle: a collar *around* the tube.
 *
 * Built from an outer wall and two end faces. A `pipeZ` here would carry an
 * inward-facing inner wall, and anything inward-facing inside this tube is drawn
 * straight into the sight picture — which is half of why the first marksman scope was
 * black.
 */
function _saddle(rOuter, rTube, z0, z1, seg = 18) {
  return mergeGeom([
    tubeZ([[rOuter, z0], [rOuter, z1]], seg),
    tubeZ([[rTube, z0], [rOuter, z0]], seg),
    tubeZ([[rOuter, z1], [rTube, z1]], seg),
  ]);
}

/** Knurled band around a tube — magnification rings, focus collars, lock nuts. */
function _knurl(r, z0, z1, seg = 16, teeth = 12) {
  const parts = [tubeZ([[r, z0], [r, z1]], seg)];
  const zc = (z0 + z1) / 2;
  const len = (z1 - z0) * 0.86;
  for (let i = 0; i < teeth; i++) {
    const a = (i / teeth) * Math.PI * 2;
    parts.push(translate(plainBox(0.0042, 0.0042, len), Math.cos(a) * r, Math.sin(a) * r, zc));
  }
  return mergeGeom(parts);
}

/** Rail clamp: the mount that puts the optic on the weapon. */
function _mount(K, o) {
  const m = [];
  const y = o.railY;
  const h = Math.max(0.006, o.y - o.footR - y);
  if (o.style === 'rings') {
    /**
     * Two separate rings — the marksman/sniper look, and the only mount that leaves
     * the rail visible between them.
     *
     * The first version made each ring a box of height `h + 0.012` centred at
     * `railY + (h + 0.012) / 2`, which put its top 12 mm *above* the bottom of the
     * tube — a chord straight across the underside of the bore. Measured, it clipped
     * every scope in the roster to between 1.25 and 2.0 degrees: the turret-saddle bug
     * again, wearing a different hat. A ring stops at the tube and then wraps it, so
     * the post runs `railY` to `o.y - footR` exactly, and the wrap is a collar built
     * the same way saddles are — outer wall plus two end faces, never a pipe.
     */
    for (const z of o.ringZ) {
      m.push(place(chamferBox(o.ringW ?? 0.034, h, 0.028, 0.0018), 0, y + h / 2, z));
      m.push(place(chamferBox((o.ringW ?? 0.034) + 0.004, 0.010, 0.032, 0.0016), 0, y + 0.005, z));
      // The collar itself, around the tube.
      const rc = o.footR + 0.0030;
      m.push(translate(_saddle(rc, o.footR, z - 0.013, z + 0.013, 16), 0, o.y, 0));
      // Ring cap screws: two bosses on top, which is what makes a ring read as a ring.
      for (const dx of [-1, 1]) {
        m.push(place(chamferBox(0.008, 0.010, 0.026, 0.0012), dx * (o.footR * 0.62), o.y + o.footR + 0.0022, z));
      }
    }
  } else if (o.style === 'cantilever') {
    // One long unimount reaching forward over the handguard — the modern LPVO look.
    m.push(place(chamferBox(0.030, h, o.len ?? 0.090, 0.0018), 0, y + h / 2, o.z));
    m.push(place(chamferBox(0.036, 0.011, 0.052, 0.0016), 0, y + 0.005, o.z + (o.len ?? 0.090) * 0.32));
    m.push(place(chamferBox(0.009, 0.012, 0.014, 0.0012), -0.020, y + 0.006, o.z + (o.len ?? 0.090) * 0.32));
  } else {
    // Integral clamp — red dots and holos.
    m.push(place(chamferBox(0.026, h, o.len ?? 0.050, 0.0016), 0, y + h / 2, o.z));
    m.push(place(chamferBox(0.030, 0.010, 0.013, 0.0016), 0, y + 0.005, o.z + (o.len ?? 0.050) * 0.38));
    m.push(place(chamferBox(0.008, 0.010, 0.011, 0.0012), -0.017, y + 0.005, o.z + (o.len ?? 0.050) * 0.38));
  }
  K.metal(mergeGeom(m));
}

/** Matte-black liner just inside a tube wall. Renders back-faces, so it is only ever
 *  seen from inside — which is the whole point. */
function _liner(K, radius, z0, z1, y, seg = 18, name = 'bore') {
  const m = new THREE.Mesh(tubeZ([[radius, z0], [radius, z1]], seg), K.M.bore);
  m.position.y = y;
  m.frustumCulled = false;
  m.renderOrder = 8;
  K.parts[name] = m;
  return m;
}

function _lens(K, r, z, y, seg, dir, order, mat = 'lens') {
  const m = new THREE.Mesh(discZ(r, z, seg, dir), K.M[mat]);
  m.position.y = y;
  m.frustumCulled = false;
  m.renderOrder = order;
  return m;
}

/**
 * Scope shadow: the ring of black that creeps in from the edge of a magnified ocular.
 * It is the single strongest cue that a sight magnifies, because it is the one thing a
 * red dot never has.
 */
function _scopeShadow(K, r, z, y) {
  const sh = new THREE.Mesh(
    new THREE.CircleGeometry(r, 32),
    new THREE.MeshBasicMaterial({
      map: vignetteTexture(),
      color: 0x000000,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
    })
  );
  sh.position.set(0, y, z);
  sh.renderOrder = 40;
  sh.frustumCulled = false;
  K.parts.scopeShadow = sh;
  return sh;
}

/* -------------------------------------------------------------------------- */
/* reticles                                                                    */
/* -------------------------------------------------------------------------- */

function _flat(mat, w, h, x, y) {
  const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), mat);
  m.position.set(x, y, 0);
  return m;
}

/**
 * Reticle factory. Every reticle is drawn with depth test off so it reads through the
 * glass, which is what a collimated sight physically does and what an etched one looks
 * like anyway.
 */
function _reticle(K, style, o) {
  const g = new THREE.Group();
  const M = K.M;
  const dotMat = o.green ? M.dotGreen : M.dot;
  const s = o.scale ?? 1;

  if (style === 'dot') {
    g.add(new THREE.Mesh(new THREE.CircleGeometry(0.0016 * s, 10), dotMat));
  } else if (style === 'dotRing') {
    // A dot inside a segmented ring: the reticle you can still find after twenty
    // rounds have walked the muzzle off target, which a bare dot is not.
    g.add(new THREE.Mesh(new THREE.CircleGeometry(0.0016 * s, 10), dotMat));
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
      const seg = new THREE.Mesh(new THREE.PlaneGeometry(0.0030 * s, 0.0011 * s), dotMat);
      seg.position.set(Math.cos(a) * 0.0072 * s, Math.sin(a) * 0.0072 * s, 0);
      seg.rotation.z = a + Math.PI / 2;
      g.add(seg);
    }
  } else if (style === 'circleDot') {
    // Holographic 65-MOA ring around a 1-MOA dot. Built from short arc segments so the
    // ring is genuinely a ring and not a disc with a hole painted on.
    g.add(new THREE.Mesh(new THREE.CircleGeometry(0.0014 * s, 10), dotMat));
    const R = 0.0115 * s;
    for (let i = 0; i < 20; i++) {
      const a = (i / 20) * Math.PI * 2;
      const seg = new THREE.Mesh(new THREE.PlaneGeometry(0.0022 * s, 0.0009 * s), dotMat);
      seg.position.set(Math.cos(a) * R, Math.sin(a) * R, 0);
      seg.rotation.z = a + Math.PI / 2;
      g.add(seg);
    }
  } else if (style === 'chevron') {
    // An open chevron: two bars meeting at a point, with the point as the aim mark.
    for (const sx of [-1, 1]) {
      const bar = _flat(dotMat, 0.0110 * s, 0.0012 * s, sx * 0.0038 * s, 0.0030 * s);
      bar.rotation.z = sx * 0.62;
      g.add(bar);
    }
    g.add(_flat(dotMat, 0.0012 * s, 0.0060 * s, 0, -0.0072 * s));
  } else if (style === 'milDot') {
    const ink = M.ink;
    g.add(_flat(ink, 0.0008 * s, 0.030 * s, 0, 0));
    g.add(_flat(ink, 0.030 * s, 0.0008 * s, 0, 0));
    for (const d of [-0.011, -0.0055, 0.0055, 0.011]) {
      g.add(_flat(ink, 0.0022 * s, 0.0022 * s, d * s, 0));
      g.add(_flat(ink, 0.0022 * s, 0.0022 * s, 0, d * s));
    }
  } else if (style === 'christmasTree') {
    /**
     * Fine-line ranging reticle with a floating centre. The lines stop short of the
     * middle rather than crossing it: at high magnification a solid intersection
     * covers most of a torso at 200 m, and a reticle that hides the thing you are
     * aiming at is the one mistake a sniper scope cannot make.
     */
    const ink = M.ink;
    for (const sg of [-1, 1]) {
      g.add(_flat(ink, 0.0006 * s, 0.0170 * s, 0, sg * 0.0110 * s));
      g.add(_flat(ink, 0.0170 * s, 0.0006 * s, sg * 0.0110 * s, 0));
    }
    g.add(_flat(ink, 0.0010 * s, 0.0010 * s, 0, 0));
    for (const d of [-0.0140, -0.0090, -0.0045, 0.0045, 0.0090, 0.0140]) {
      g.add(_flat(ink, 0.0016 * s, 0.0016 * s, d * s, 0));
      g.add(_flat(ink, 0.0016 * s, 0.0016 * s, 0, d * s));
    }
    for (const [d, w] of [[-0.0068, 0.0060], [-0.0114, 0.0044], [-0.0160, 0.0030]]) {
      g.add(_flat(ink, w * s, 0.0006 * s, 0, d * s));
    }
  } else if (style === 'bdc') {
    // Duplex with a holdover ladder — the marksman answer: quick to pick up, and it
    // ranges. Thick outer posts, thin inner cross.
    const ink = M.ink;
    for (const sg of [-1, 1]) {
      g.add(_flat(ink, 0.0022 * s, 0.0110 * s, 0, sg * 0.0180 * s));
      g.add(_flat(ink, 0.0110 * s, 0.0022 * s, sg * 0.0180 * s, 0));
      g.add(_flat(ink, 0.0007 * s, 0.0130 * s, 0, sg * 0.0060 * s));
      g.add(_flat(ink, 0.0130 * s, 0.0007 * s, sg * 0.0060 * s, 0));
    }
    for (const [d, w] of [[-0.0055, 0.0070], [-0.0092, 0.0052], [-0.0130, 0.0036]]) {
      g.add(_flat(ink, w * s, 0.0007 * s, 0, d * s));
    }
    // Illuminated centre dot, so it still works in shadow.
    g.add(new THREE.Mesh(new THREE.CircleGeometry(0.0011 * s, 10), dotMat));
  }

  g.position.set(0, o.y, o.z);
  g.renderOrder = 60;
  g.traverse((m) => {
    if (m.isMesh) {
      m.material.depthTest = false;
      m.material.depthWrite = false;
      m.frustumCulled = false;
    }
  });
  K.parts.reticle = g;
  return g;
}

/* -------------------------------------------------------------------------- */
/* tube optics — red dots, LPVOs and scopes                                    */
/* -------------------------------------------------------------------------- */

/**
 * The one tube-optic builder.
 *
 * Profile runs objective (front, -Z) to ocular (rear, +Z):
 *
 *   zTip ── objective bell (rObj) ── shoulder ── main tube (rTube) ── ocular bell (rOc) ── zBack
 *
 * A red dot is this with `rObj === rTube === rOc` and a very short body; a 3-9x is the
 * same code with a fat bell and a magnification ring. That is deliberate — it is the
 * only way to be sure a red dot cannot acquire the scope's bugs and vice versa.
 */
function tubeOptic(K, o) {
  const y = o.y;
  const rTube = o.rTube;
  const wall = o.wall ?? 0.0028;
  /**
   * The clear radius inside the tube.
   *
   * NOT `rTube - wall`. The tube is a single-sided surface of revolution, so from
   * inside the bore it has no thickness at all — its wall is back-facing and never
   * rasterised, and the liner *is* the inner surface. Subtracting a wall thickness
   * here throws away 3 mm of radius for nothing, which measured out as 0.6 degrees of
   * sight picture on the marksman rifles: enough to drop them below the 3.50 the
   * shipped DMR already managed. `wall` is used for the visible rims at each end,
   * where there really is a step, and nowhere else.
   */
  const rIn = rTube - (o.linerGap ?? 0.0008);
  const rObj = o.rObj ?? rTube;
  const rOc = o.rOc ?? rTube;
  const zTip = o.zTip;
  const zBack = o.zBack;
  const objLen = o.objLen ?? 0;
  const ocLen = o.ocLen ?? 0;
  const taper = o.taper ?? 0.026;

  // Glass radii: the clear aperture at each end, inside the rim.
  const rObjGlass = o.rObjGlass ?? rObj - wall;
  const rOcGlass = o.rOcGlass ?? rOc - wall;

  const zObjEnd = zTip + objLen; // rear of the objective bell
  const zShoulder = zObjEnd + (rObj > rTube ? taper : 0); // where the tube proper starts
  const zOcStart = zBack - ocLen; // front of the ocular bell
  const zOcTaper = zOcStart - (rOc > rTube ? taper * 0.6 : 0);

  const body = [];
  const profile = [[rObjGlass, zTip], [rObj, zTip + 0.004]];
  if (rObj > rTube) {
    profile.push([rObj, zObjEnd], [rTube, zShoulder]);
  }
  profile.push([rTube, zOcTaper]);
  if (rOc > rTube) profile.push([rOc, zOcStart]);
  profile.push([rOc, zBack - 0.004], [rOcGlass, zBack]);
  body.push(tubeZ(profile, o.seg ?? 18));

  /**
   * Inner wall of the ocular bell, so the rear lens sits in a real recess. The
   * objective end deliberately gets none: it is a quarter of a metre down the sight
   * line, and an inward-facing ring there draws a bright halo right in the middle of
   * the sight picture.
   */
  if (rOc > rTube) body.push(tubeZ([[rOcGlass, zBack], [rOcGlass, zBack - 0.0022]], 16));

  /* ------------------------------- turrets -------------------------------- */
  const tz = o.turretZ ?? (zShoulder + zOcTaper) / 2;
  if (o.turrets !== false) {
    const th = o.turretH ?? 0.0184;
    const tr = o.turretR ?? rTube * 0.72;
    body.push(_lathedKnob(rTube + 0.0016, th, tr, tz, 'up', rTube));
    body.push(_lathedKnob(rTube + 0.0016, th * 0.89, tr * 0.91, tz, 'left', rTube));
    // Saddle: a collar around the tube, never a plug through it.
    body.push(_saddle(rTube + 0.0058, rTube, tz - 0.009, tz + 0.009, o.seg ?? 18));
    // Bosses for the turret bases, kept clear of the bore for the same reason.
    body.push(place(chamferBox(tr * 1.9, 0.010, 0.018, 0.0012), 0, rTube + 0.0050, tz));
    body.push(place(chamferBox(0.010, tr * 1.9, 0.018, 0.0012), -(rTube + 0.0050), 0, tz));
  }
  if (o.parallax) {
    body.push(_lathedKnob(rTube + 0.0016, 0.0164, rTube * 0.60, tz - 0.032, 'left', rTube));
  }
  if (o.batteryCap) {
    /**
     * Outside the tube wall, not on it.
     *
     * At `rTube * 0.92` the boss's inboard face sat 8 mm inside the bore and it was
     * the limiting obstruction on four weapons — measured as a hit at model x = 0.011
     * inside a 19.2 mm tube. Anything bolted to a tube starts at the tube's radius;
     * there is no exception for small parts.
     */
    body.push(place(chamferBox(0.014, 0.008, 0.024, 0.0010), rTube + 0.0062, 0.006, tz + 0.006));
  }
  if (o.magRing) {
    const z0 = zOcTaper - 0.020;
    body.push(_knurl(rOc > rTube ? rOc - 0.0022 : rTube + 0.0028, z0, z0 + 0.020, o.seg ?? 18, 12));
    if (o.throwLever) body.push(place(chamferBox(0.012, 0.030, 0.014, 0.0014), -(rOc - 0.0026), 0.014, z0 + 0.010));
  }

  const bg = mergeGeom(body);
  translate(bg, 0, y, 0);
  K.metal(bg);

  /* -------------------------------- optics -------------------------------- */
  /**
   * Bore liner.
   *
   * Its front rim sits at whatever was *already* the tightest aperture — the objective
   * shoulder on a scope, the front rim on a red dot — 0.6 to 0.8 mm inside it. That
   * costs about 2% of the clear cone and buys a flat-black tube: without it, a scope
   * pointed at bright sky shows a ring of sunlit saddle end-faces and turret bases
   * with a small disc of world in the middle. Held less than 0.5 mm clear the two
   * surfaces z-fight and speckle the inside of the tube with lit gunmetal.
   */
  const linerR = rIn;
  const linerZ0 = rObj > rTube ? zShoulder - 0.0005 : zTip + 0.0006;
  _liner(K, linerR, linerZ0, zBack - 0.0006, y, o.seg ?? 18);

  /**
   * The objective shoulder, blacked out.
   *
   * The cone where the bell steps down to the tube is the one inward-facing surface
   * the eye can see all the way down the bore, and in gunmetal it renders as a lit
   * grey plate covering most of the sight picture. Shadowed 0.3 mm inside so it
   * occludes without changing the clear cone.
   */
  if (rObj > rTube) {
    const s = new THREE.Mesh(
      tubeZ([[rObj - 0.0003, zTip + 0.005], [rObj - 0.0003, zObjEnd], [rTube - 0.0003, zShoulder]], o.seg ?? 18),
      K.M.bore
    );
    s.position.y = y;
    s.frustumCulled = false;
    s.renderOrder = 8;
    K.parts.boreFront = s;
  }

  const lensMat = o.amber ? 'lensAmber' : 'lens';
  K.parts.lensFront = _lens(K, rObjGlass, zTip + 0.0012, y, o.seg ?? 18, -1, 20, lensMat);
  K.parts.lensRear = _lens(K, rOcGlass, zBack - 0.0012, y, o.seg ?? 18, 1, 21, lensMat);

  if (o.magnified) _scopeShadow(K, rOcGlass * 1.03, zBack + 0.0002, y);

  /* ------------------------------- apertures ------------------------------ */
  // Everything that can clip the sight picture, front to back.
  const apertures = [
    { r: rObjGlass, z: zTip + 0.0012, what: 'objective glass' },
    { r: linerR, z: linerZ0, what: 'bore liner front rim' },
    { r: rOcGlass, z: zBack - 0.0012, what: 'ocular glass' },
  ];

  return {
    apertures,
    marks: [],
    ocularR: rOcGlass,
    ocularZ: zBack - 0.0012,
    topY: y + Math.max(rOc, rObj, rTube + 0.0016 + (o.turrets !== false ? (o.turretH ?? 0.0184) : 0)),
    tubeR: rTube,
    turretZ: tz,
  };
}

/* -------------------------------------------------------------------------- */
/* the optic catalogue                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Build a sight.
 *
 * @param K    the weapon kit being assembled
 * @param spec { kind, y, z, railY, ... } — `y` is the height of the optical axis above
 *             the bore, which is the number every clearance check on the weapon is
 *             measured against.
 * @returns    { sightY, aimZ, collimated, reticleClear, apertures, ocularR, ocularZ, topY }
 */
export function buildOptic(K, spec) {
  const kind = spec.kind;
  const y = spec.y;
  const z = spec.z ?? 0;

  /* ------------------------------- red dots -------------------------------- */
  if (kind === 'reflex' || kind === 'micro' || kind === 'tubeDot') {
    const big = kind === 'reflex';
    const rTube = spec.rTube ?? (big ? 0.0192 : 0.0170);
    const len = spec.len ?? (big ? 0.058 : 0.044);
    const zTip = z - len / 2;
    const zBack = z + len / 2;
    _mount(K, { railY: spec.railY, y, footR: rTube, style: 'clamp', z: z + 0.004, len: len * 0.86 });
    // End rings: the raised rims that make a red dot read as a machined housing
    // rather than a length of pipe.
    const rim = [];
    rim.push(tubeZ([[rTube, zTip + 0.001], [rTube + 0.0020, zTip + 0.003], [rTube + 0.0020, zTip + 0.010]], 16));
    rim.push(tubeZ([[rTube + 0.0020, zBack - 0.010], [rTube + 0.0020, zBack - 0.003], [rTube, zBack - 0.001]], 16));
    K.metal(translate(mergeGeom(rim), 0, y, 0));

    const t = tubeOptic(K, {
      y,
      rTube,
      wall: 0.0026,
      zTip,
      zBack,
      turrets: true,
      turretH: 0.0128,
      turretR: rTube * 0.62,
      turretZ: z,
      batteryCap: true,
      seg: 16,
    });
    _reticle(K, spec.reticle ?? 'dot', { y, z, green: spec.green });
    return {
      sightY: y,
      aimZ: z,
      collimated: true,
      reticleClear: t.ocularR * 0.84,
      ...t,
    };
  }

  /* ------------------------------ holographic ------------------------------ */
  if (kind === 'holo') {
    /**
     * A holographic sight is a *box*, not a tube, and that rectangular window is the
     * only reason it reads differently from a red dot at a glance. It is built as four
     * walls around a square aperture, with the interior lined in matte black exactly
     * the way a tube is — a square `tubeZ` (four segments, rotated 45 degrees so the
     * faces land flat) does the job with the same back-facing trick.
     */
    const half = spec.half ?? 0.0182; // half-width of the clear window
    const wall = 0.0032;
    const len = spec.len ?? 0.070;
    const zTip = z - len / 2;
    const zBack = z + len / 2;
    const outer = half + wall;

    _mount(K, { railY: spec.railY, y, footR: outer, style: 'clamp', z: z + 0.010, len: len * 0.72 });

    const b = [];
    for (const sx of [-1, 1]) b.push(place(chamferBox(wall, outer * 2.0, len, 0.0012), sx * (half + wall / 2), 0, z));
    b.push(place(chamferBox(outer * 2.0, wall, len, 0.0012), 0, outer - wall / 2, z)); // hood
    b.push(place(chamferBox(outer * 2.0, wall * 1.6, len, 0.0014), 0, -(outer - wall * 0.8), z)); // base
    /**
     * Battery/laser housing.
     *
     * This started as a box directly behind the rear window and it was a plug: every
     * ray down the sight line terminated on its front face, so the sight was 100%
     * opaque — the same failure as the marksman rifle's turret saddle, in a different
     * shape. On a real holographic sight you look *through* the rear of the hood; the
     * body hangs underneath and reaches back, which is what gives the design its long,
     * low, back-heavy profile in the first place. Nothing sits behind the window.
     */
    b.push(place(chamferBox(outer * 1.7, outer * 0.9, 0.052, 0.0016), 0, -(outer + wall), zBack + 0.008));
    b.push(place(chamferBox(0.014, 0.016, 0.016, 0.0012), outer * 0.6, -(outer + wall * 0.4), zBack + 0.026)); // buttons
    b.push(place(chamferBox(0.014, 0.016, 0.016, 0.0012), outer * 0.6, -(outer + wall * 0.4), zBack + 0.008));
    K.metal(translate(mergeGeom(b), 0, y, 0));

    // Matte-black liner: a square tube rotated so its flats face the walls.
    const linerR = (half - 0.0006) * Math.SQRT2;
    const lin = tubeZ([[linerR, zTip + 0.0008], [linerR, zBack - 0.0008]], 4);
    rotate(lin, 0, 0, Math.PI / 4);
    const linMesh = new THREE.Mesh(lin, K.M.bore);
    linMesh.position.y = y;
    linMesh.frustumCulled = false;
    linMesh.renderOrder = 8;
    K.parts.bore = linMesh;

    const glass = (zz, dir, order) => {
      const m = new THREE.Mesh(new THREE.PlaneGeometry(half * 2, half * 2), K.M.lens);
      m.position.set(0, y, zz);
      if (dir < 0) m.rotation.y = Math.PI;
      m.renderOrder = order;
      m.frustumCulled = false;
      return m;
    };
    K.parts.lensFront = glass(zTip + 0.0016, -1, 20);
    K.parts.lensRear = glass(zBack - 0.0016, 1, 21);

    _reticle(K, spec.reticle ?? 'circleDot', { y, z, green: spec.green ?? true });

    const apertures = [
      { r: half - 0.0006, z: zTip + 0.0016, what: 'holo front window' },
      { r: half - 0.0006, z: zBack - 0.0016, what: 'holo rear window' },
    ];
    return {
      sightY: y,
      aimZ: z,
      collimated: true,
      reticleClear: half * 0.80,
      apertures,
      marks: [],
      ocularR: half - 0.0006,
      ocularZ: zBack - 0.0016,
      topY: y + outer,
    };
  }

  /* ---------------------------- magnified glass ---------------------------- */
  if (kind === 'lpvo' || kind === 'scope') {
    const big = kind === 'scope';
    const rTube = spec.rTube ?? (big ? 0.0238 : 0.0190);
    const rObj = spec.rObj ?? (big ? 0.0300 : 0.0265);
    const rOc = spec.rOc ?? (big ? 0.0250 : 0.0212);
    const zTip = spec.zTip ?? (big ? -0.152 : -0.120);
    const zBack = spec.zBack ?? (big ? 0.101 : 0.096);
    _mount(K, {
      railY: spec.railY,
      y,
      footR: rTube,
      style: 'rings',
      ringZ: spec.ringZ ?? [zTip + 0.078, zBack - 0.056],
      ringW: big ? 0.034 : 0.030,
    });
    const t = tubeOptic(K, {
      y,
      rTube,
      wall: 0.0030,
      rObj,
      rOc,
      zTip,
      zBack,
      objLen: big ? 0.046 : 0.048,
      ocLen: big ? 0.046 : 0.030,
      taper: big ? 0.026 : 0.008,
      turrets: true,
      turretH: big ? 0.0246 : 0.0184,
      turretR: big ? 0.0150 : 0.0138,
      turretZ: spec.turretZ ?? -0.030,
      parallax: big,
      magRing: true,
      throwLever: big,
      magnified: true,
      seg: 18,
    });
    // The reticle sits in the tube at the ocular focal plane, not at infinity.
    const retZ = zBack - (big ? 0.007 : 0.006);
    _reticle(K, spec.reticle ?? (big ? 'christmasTree' : 'bdc'), { y, z: retZ, scale: spec.reticleScale ?? 1 });
    return {
      sightY: y,
      aimZ: spec.aimZ ?? zBack - 0.026,
      collimated: false,
      reticleClear: 0,
      ...t,
    };
  }

  /* ------------------------------ iron sights ------------------------------ */
  if (kind === 'aperture') {
    /**
     * Ghost-ring aperture and an open front post.
     *
     * Two lessons are baked into the numbers here.
     *
     * The rear ring started at 4 mm: at aiming eye relief that is a 4.3-degree
     * peephole, and a peephole is exactly what an aperture sight must *not* read as on
     * screen. A real diopter works because the eye focuses past the blurred rim; we
     * have no way to blur it, so the game equivalent is the ghost ring — a wide hole
     * with a thin rim the eye ignores. 12–13 mm is what puts the clear cone above 4
     * degrees, which is where the optics sit.
     *
     * The front sight is deliberately NOT hooded. A closed hood ring 39 cm from the
     * eye with a 10 mm bore is a 1.0-degree tunnel — measured — and that is a worse
     * sight picture than anything this project has shipped, optics included. Real
     * protective wings are open at the top for the same reason; here they are open at
     * the top *and* held below the post's tip, so the only thing on the sight axis is
     * the post itself, which is the aiming mark and is supposed to be there.
     */
    const rIn = spec.aperture ?? 0.0126;
    const rOut = rIn + 0.0032;
    const zr = spec.rearZ ?? 0.026;
    const zf = spec.frontZ ?? -0.242;
    K.metal(translate(pipeZ(rOut, rIn, zr - 0.008, zr + 0.008, 16), 0, y, 0));
    _liner(K, rIn - 0.0005, zr - 0.0085, zr + 0.0085, y, 16);
    // Windage drum: a knurled wheel behind the aperture. Distinctive and cheap.
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      K.metal(translate(plainBox(0.0035, 0.0035, 0.014), Math.cos(a) * rOut, y + Math.sin(a) * rOut, zr + 0.006));
    }
    K.metal(place(chamferBox(0.020, 0.014, 0.014, 0.0014), 0, y - rOut - 0.006, zr + 0.006));
    // Front post on a block, flanked by two thin wings that stop below the tip.
    const wingX = spec.wing ?? 0.0110;
    K.metal(place(chamferBox(0.024, 0.014, 0.020, 0.0014), 0, y - 0.021, zf)); // base block
    for (const sx of [-1, 1]) {
      K.metal(place(chamferBox(0.0040, 0.020, 0.012, 0.0010), sx * wingX, y - 0.0122, zf));
    }
    K.metal(place(chamferBox(0.0024, 0.0130, 0.0038, 0.0006), 0, y - 0.0065, zf)); // the post
    K.parts.reticle = null;
    return {
      sightY: y,
      aimZ: zr,
      collimated: false,
      reticleClear: 0,
      apertures: [{ r: rIn - 0.0005, z: zr, what: 'rear ghost ring' }],
      marks: [{ r: wingX - 0.0020, z: zf, what: 'front post + wings' }],
      ocularR: rIn - 0.0005,
      ocularZ: zr,
      topY: y + rOut,
    };
  }

  if (kind === 'notch') {
    /**
     * Open tangent notch and a hooded post — the AK answer, and the only sight in the
     * roster with no rim around the sight picture at all. Its clear cone is limited by
     * the front sight ears, so they are cut low and kept thin.
     */
    const zr = spec.rearZ ?? 0.086;
    const zf = spec.frontZ ?? -0.300;
    const leaf = [];
    leaf.push(place(chamferBox(0.030, 0.010, 0.044, 0.0014), 0, y - 0.012, zr - 0.008)); // tangent base
    for (const sx of [-1, 1]) leaf.push(place(chamferBox(0.006, 0.014, 0.008, 0.0012), sx * 0.010, y - 0.001, zr));
    leaf.push(place(chamferBox(0.026, 0.008, 0.010, 0.0012), 0, y - 0.010, zr)); // notch floor
    leaf.push(place(chamferBox(0.034, 0.014, 0.012, 0.0014), 0, y - 0.016, zr + 0.026)); // slider
    K.metal(mergeGeom(leaf));
    // Front post between two ears, open at the top.
    const ear = spec.ear ?? 0.0110;
    K.metal(place(chamferBox(0.024, 0.014, 0.020, 0.0014), 0, y - 0.020, zf)); // block
    for (const sx of [-1, 1]) K.metal(place(chamferBox(0.0048, 0.024, 0.014, 0.0010), sx * ear, y - 0.002, zf));
    K.metal(translate(rodZ(0.0022, zf - 0.006, zf + 0.006, 8), 0, y - 0.004, 0)); // post
    K.parts.reticle = null;
    return {
      sightY: y,
      aimZ: zr,
      collimated: false,
      reticleClear: 0,
      /**
       * An open notch is the only sight in the roster with no rim around the sight
       * picture at all — nothing forms a tunnel, so there is no aperture to report.
       * The ears are an on-axis mark, like the post they protect: intended geometry,
       * counted separately from the unintended kind this whole measurement exists to
       * catch.
       */
      apertures: [],
      // The rear notch body and the front ears are both sight furniture: you look
      // *over* a notch, so the leaf under the axis is as much part of the sight as the
      // post is. Reported as marks, not as obstructions.
      marks: [
        { r: ear - 0.0024, z: zf, what: 'front sight ears' },
        { r: 0.015, z0: zr - 0.030, z1: zr + 0.030, what: 'rear notch leaf' },
      ],
      ocularR: 0.010,
      ocularZ: zr,
      topY: y + 0.012,
    };
  }

  if (kind === 'ghost') {
    // Shotgun ghost ring plus a bright front bead. A ghost ring's whole point is that
    // the rim is too big and too close to focus on, so the eye ignores it and centres
    // the bead by itself.
    const rIn = spec.aperture ?? 0.0126;
    const zr = spec.rearZ ?? 0.062;
    const zf = spec.frontZ ?? -0.436;
    K.metal(place(ringZ(rIn + 0.0020, 0.0020, 16, 6), 0, y, zr));
    // The base post hangs *below* the ring's own aperture. At `y - 0.018` its top sat
    // 2.5 mm inside the ring — a partial plug that clipped the sight to 3.25 degrees
    // when the ring itself was good for 4.99.
    K.metal(place(chamferBox(0.020, 0.018, 0.012, 0.0014), 0, y - rIn - 0.011, zr));
    K.metal(place(chamferBox(0.0026, 0.0140, 0.0040, 0.0006), 0, y - 0.007, zf));
    K.metal(place(chamferBox(0.016, 0.014, 0.014, 0.0012), 0, y - 0.021, zf));
    const bead = new THREE.Mesh(new THREE.SphereGeometry(0.0022, 8, 6), K.M.glow);
    bead.position.set(0, y, zf);
    bead.renderOrder = 60;
    bead.material.depthTest = false;
    bead.frustumCulled = false;
    K.parts.reticle = bead;
    return {
      sightY: y,
      aimZ: zr,
      collimated: false,
      reticleClear: 0,
      apertures: [{ r: rIn, z: zr, what: 'ghost ring' }],
      marks: [{ r: 0.0026, z: zf, what: 'front bead' }],
      ocularR: rIn,
      ocularZ: zr,
      topY: y + rIn + 0.004,
    };
  }

  if (kind === 'bead') {
    // A bead on a rib — the break-action's only sight, and correctly the sparsest
    // thing in the file. Nothing is in the way, so the clear cone is the whole screen.
    const zf = spec.frontZ ?? -0.300;
    K.metal(place(chamferBox(0.010, 0.005, spec.ribLen ?? 0.240, 0.0008), 0, y - 0.004, (zf + (spec.rearZ ?? 0.06)) / 2));
    const bead = new THREE.Mesh(new THREE.SphereGeometry(0.0026, 8, 6), K.M.glow);
    bead.position.set(0, y, zf);
    bead.renderOrder = 60;
    bead.material.depthTest = false;
    bead.frustumCulled = false;
    K.parts.reticle = bead;
    return {
      sightY: y,
      aimZ: spec.rearZ ?? 0.06,
      collimated: false,
      reticleClear: 0,
      apertures: [],
      /**
       * The rib *is* the sight.
       *
       * A bead on a rib is aimed by looking along the rib, so the rib sits a couple of
       * millimetres under the line of sight for its whole length by design — exactly
       * as a real sighting plane does. Counting it as an obstruction would report this
       * weapon at 0.00 degrees for doing the one thing it is supposed to do. It is
       * furniture, and the span it occupies is declared here.
       */
      marks: [{ r: 0.010, z0: zf - 0.020, z1: (spec.rearZ ?? 0.06) + 0.020, what: 'sighting rib' }],
      ocularR: 0.020,
      ocularZ: spec.rearZ ?? 0.06,
      topY: y + 0.004,
    };
  }

  throw new Error(`[optics] unknown sight kind "${kind}"`);
}
